import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NotificationService } from '../../../../common/services/notification/notification.service';
import { FilterOptionsService } from './filter-options.service';
import {
  ConsigneePlantFilterOptionsLoad,
  InboundPlantFilterOptionsLoad,
  FilterOptionsInitialState,
  SapIdOptionsLoad,
  ShipToPlantFilterOptionsLoad,
  BusinessUnitsOptionsLoad,
  NetworkPartOptionsLoad,
  ConsignorAddressOptionsLoad,
  FreightPayerOptionsLoad,
  SourceLocationsOptionsLoad,
  DestinationLocationsOptionsLoad,
  PortOfLoadingOptionsLoad,
  PortOfDischargeOptionsLoad,
} from './filter-options.actions';
import { Error, CustomHttpErrorResponse } from '../../../../common';
import {
  MonitoringFilterGateOption,
  MonitoringFilterPlantOption,
} from '../models/monitoring-filter-plant-option.model';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface FilterOptionsStateModel {
  inboundPlantOptions: string[];
  shipToPlantOptions: MonitoringFilterGateOption[];
  consigneePlantOptions: MonitoringFilterPlantOption[];
  sapIdOptions: string[];
  businessUnitOptions: string[];
  networkPartOptions: string[];
  consignorAddressOptions: { name: string; city: string; country: string }[];
  freightPayersOptions: { extId: string; city: string }[];
  sourceLocationsOptions: string[];
  destinationLocationsOptions: string[];
  portsOfLoadingOptions: { abbreviation: string; cityName: string }[];
  portsOfDischargeOptions: { abbreviation: string; cityName: string }[];
  loaded: boolean;
}

const initialState: FilterOptionsStateModel = {
  inboundPlantOptions: [],
  shipToPlantOptions: [],
  consigneePlantOptions: [],
  sapIdOptions: [],
  businessUnitOptions: [],
  networkPartOptions: [],
  consignorAddressOptions: [],
  freightPayersOptions: [],
  sourceLocationsOptions: [],
  destinationLocationsOptions: [],
  portsOfLoadingOptions: [],
  portsOfDischargeOptions: [],
  loaded: false,
};

@State<FilterOptionsStateModel>({
  name: 'filterOptions',
  defaults: initialState,
})
@Injectable()
export class FilterOptionsState {
  constructor(
    private plantFilterOptionsService: FilterOptionsService,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getInboundPlantOptions(state: FilterOptionsStateModel): string[] {
    return state.inboundPlantOptions;
  }

  @Selector()
  static getShipToPlantOptions(state: FilterOptionsStateModel): MonitoringFilterGateOption[] {
    return state.shipToPlantOptions;
  }

  @Selector()
  static getConsigneePlantOptions(state: FilterOptionsStateModel): MonitoringFilterPlantOption[] {
    return state.consigneePlantOptions;
  }
  @Selector()
  static sapIdOptions(state: FilterOptionsStateModel): string[] {
    return state.sapIdOptions;
  }

  @Selector()
  static businessUnitOptions(state: FilterOptionsStateModel): string[] {
    return state.businessUnitOptions;
  }

  @Selector()
  static networkPartOptions(state: FilterOptionsStateModel): string[] {
    return state.networkPartOptions;
  }

  @Selector()
  static consignorAddressOptions(state: FilterOptionsStateModel): { name: string; city: string; country: string }[] {
    return state.consignorAddressOptions;
  }

  @Selector()
  static freightPayersOptions(state: FilterOptionsStateModel): { extId: string; city: string }[] {
    return state.freightPayersOptions;
  }

  @Selector()
  static sourceLocationsOptions(state: FilterOptionsStateModel): string[] {
    return state.sourceLocationsOptions;
  }

  @Selector()
  static destinationLocationsOptions(state: FilterOptionsStateModel): string[] {
    return state.destinationLocationsOptions;
  }

  @Selector()
  static portsOfLoadingOptions(state: FilterOptionsStateModel): { abbreviation: string; cityName: string }[] {
    return state.portsOfLoadingOptions;
  }

  @Selector()
  static portsOfDischargeOptions(state: FilterOptionsStateModel): { abbreviation: string; cityName: string }[] {
    return state.portsOfDischargeOptions;
  }

  @Selector()
  static getLoaded(state: FilterOptionsStateModel): boolean {
    return state.loaded;
  }

  @Action(InboundPlantFilterOptionsLoad)
  getInboundPlantFilterOptions(
    ctx: StateContext<FilterOptionsStateModel>,
    { payload }: InboundPlantFilterOptionsLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.plantFilterOptionsService.getInboundPlantOptions(payload).pipe(
      map((inboundPlantOptions: string[]) => {
        ctx.patchState({ inboundPlantOptions, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ShipToPlantFilterOptionsLoad)
  getShipToPlantFilterOptions(
    ctx: StateContext<FilterOptionsStateModel>,
    { payload }: ShipToPlantFilterOptionsLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.plantFilterOptionsService.getGateOptions(payload).pipe(
      map((shipToPlantOptions: MonitoringFilterGateOption[]) => {
        ctx.patchState({
          shipToPlantOptions: [
            ...shipToPlantOptions.map((option: MonitoringFilterGateOption) => new MonitoringFilterGateOption(option)),
          ],
          loaded: true,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ConsigneePlantFilterOptionsLoad)
  getConsigneePlantFilterOptions(
    ctx: StateContext<FilterOptionsStateModel>,
    { payload }: ConsigneePlantFilterOptionsLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.plantFilterOptionsService.getPlantOptions(payload).pipe(
      map((consigneePlantOptions: MonitoringFilterPlantOption[]) => {
        ctx.patchState({
          consigneePlantOptions: [
            ...consigneePlantOptions.map(
              (option: MonitoringFilterPlantOption) => new MonitoringFilterPlantOption(option),
            ),
          ],
          loaded: true,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(SapIdOptionsLoad)
  getSapIdOptions(
    ctx: StateContext<FilterOptionsStateModel>,
    { payload }: SapIdOptionsLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.plantFilterOptionsService.getSapIdOptions(payload).pipe(
      map((sapIdOptions: string[]) => {
        ctx.patchState({ sapIdOptions, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(BusinessUnitsOptionsLoad)
  getBusinessUnitsOptions(
    ctx: StateContext<FilterOptionsStateModel>,
    { payload }: BusinessUnitsOptionsLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.plantFilterOptionsService.getBusinessUnitsOptions(payload).pipe(
      map((gbOptions: string[]) => {
        ctx.patchState({ businessUnitOptions: gbOptions, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(NetworkPartOptionsLoad)
  getNetworkPartOptions(
    ctx: StateContext<FilterOptionsStateModel>,
    { payload }: NetworkPartOptionsLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.plantFilterOptionsService.getNetworkPartOptions(payload).pipe(
      map((networkParts: string[]) => {
        ctx.patchState({ networkPartOptions: networkParts, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ConsignorAddressOptionsLoad)
  getConsignorAddressOptions(
    ctx: StateContext<FilterOptionsStateModel>,
    { payload }: ConsignorAddressOptionsLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.plantFilterOptionsService.getConsignorAddressOptions(payload).pipe(
      map((consignorAddressOptions: { name: string; city: string; country: string }[]) => {
        ctx.patchState({ consignorAddressOptions, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(FreightPayerOptionsLoad)
  getFreightPayerOptions(
    ctx: StateContext<FilterOptionsStateModel>,
    { payload }: ConsignorAddressOptionsLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.plantFilterOptionsService.getFreightPayersOptions(payload).pipe(
      map((freightPayersOptions: { extId: string; city: string }[]) => {
        ctx.patchState({ freightPayersOptions, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(SourceLocationsOptionsLoad)
  getSourceLocationsOptions(
    ctx: StateContext<FilterOptionsStateModel>,
    { payload }: SourceLocationsOptionsLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.plantFilterOptionsService.getSourceLocationsOptions(payload).pipe(
      map((sourceLocationsOptions: string[]) => {
        ctx.patchState({ sourceLocationsOptions, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(DestinationLocationsOptionsLoad)
  getDestinationLocationsOptions(
    ctx: StateContext<FilterOptionsStateModel>,
    { payload }: DestinationLocationsOptionsLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.plantFilterOptionsService.getDestinationLocationsOptions(payload).pipe(
      map((destinationLocationsOptions: string[]) => {
        ctx.patchState({ destinationLocationsOptions, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(PortOfLoadingOptionsLoad)
  getPortOfLoadingOptions(
    ctx: StateContext<FilterOptionsStateModel>,
    { payload }: PortOfLoadingOptionsLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.plantFilterOptionsService.getPortOfLoadingAndDischargeOptions(payload).pipe(
      map((portsOfLoadingOptions: { abbreviation: string; cityName: string }[]) => {
        ctx.patchState({ portsOfLoadingOptions, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(PortOfDischargeOptionsLoad)
  getPortOfDischargeOptions(
    ctx: StateContext<FilterOptionsStateModel>,
    { payload }: PortOfDischargeOptionsLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.plantFilterOptionsService.getPortOfLoadingAndDischargeOptions(payload).pipe(
      map((portsOfDischargeOptions: { abbreviation: string; cityName: string }[]) => {
        ctx.patchState({ portsOfDischargeOptions, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(FilterOptionsInitialState)
  setInitialState(ctx: StateContext<FilterOptionsStateModel>): void {
    ctx.setState(initialState);
  }
}
